import React from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Fab,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DivideOrderModal from "../OrderActions/DivideOrderModal";
import EditOrderFormCaptinOrder from "../OrderActions/EditOrderFormCaptinOrder";
import EditOrderFormClint from "../OrderActions/EditOrderFormClint";
import EditOrderFormTableNumber from "../OrderActions/EditOrderFormTableNumber";
import { alpha } from "@mui/material/styles";
import { useSettings } from "../../../../../../../../context/SettingsContext";
import { changeOrderStatusApi } from "../../../../../../../../services/OrderStatus/OrderStatusApi";
import { usePermissions } from "../../../../../../../../context/PermissionsAndFeatures/UserPermissionsContext";

const OrderInfoCard = ({ details, onEdit, onDelete, tables = [] }) => {
  const customer = details.customer || {};
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // existing settings and state
  const { settings, loading, error } = useSettings();
  const statusLabels = {
    under_review: "قيد المراجعه",
    pending: "طلب مفتوح",
    open: "مفتوح",
    closed: "مغلق",
    completed: "مكتمل",
    cancelled: "ملغي",
    "اوردر خارجي": "اوردر خارجي",
    order_under_review: "قيد المراجعه",
  };

  const [currentStatus, setCurrentStatus] = React.useState(details.status || "");
  React.useEffect(() => {
    setCurrentStatus(details.status || "");
  }, [details.status]);

  const isUnderReview = (currentStatus === "under_review" || currentStatus === "order_under_review");

  const orderNumberPreference = settings?.invoice_incremant || "general_increment";
  const { hasPermission } = usePermissions();

  const getOrderNumber = () => {
    const map = {
      general: { key: "general_increment", label: "عام" },
      daily: { key: "daily_increment", label: "يومي" },
      monthly: { key: "monthly_increment", label: "شهري" },
      general_increment: { key: "general_increment", label: "عام" },
      daily_increment: { key: "daily_increment", label: "يومي" },
      monthly_increment: { key: "monthly_increment", label: "شهري" },
    };
    const cfg = map[orderNumberPreference] || map.general;
    return { value: details[cfg.key] ?? "-", label: cfg.label };
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const parts = timeStr.split(":");
    const h = Number(parts[0] ?? 0);
    const m = parts[1] ?? "00";
    const ampm = h >= 12 ? "م" : "ص";
    const hour12 = h % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  // state for menus and modals
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const [divideOpen, setDivideOpen] = React.useState(false);
  const [editCaptainOpen, setEditCaptainOpen] = React.useState(false);
  const [editCaptainData, setEditCaptainData] = React.useState(null);
  const [editClientOpen, setEditClientOpen] = React.useState(false);
  const [editClientData, setEditClientData] = React.useState(null);
  const [editTableOpen, setEditTableOpen] = React.useState(false);
  const [editTableData, setEditTableData] = React.useState(null);

  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [confirmSuccess, setConfirmSuccess] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState("");
  const [confirmMenuAnchor, setConfirmMenuAnchor] = React.useState(null);

  const handleConfirm = async (status) => {
    if (!details?.id) return;
    try {
      setConfirmMenuAnchor(null);
      setConfirmLoading(true);
      await changeOrderStatusApi(details.id,);
      setCurrentStatus(status);
      setConfirmSuccess(true);
      if (typeof window.refreshOrderDetails === "function") {
        window.refreshOrderDetails(details.id);
      }
    } catch (err) {
      console.error(err);
      setConfirmError("فشل في تأكيد الطلب. حاول مرة أخرى.");
    } finally {
      setConfirmLoading(false);
    }
  };

  // use the media query result for mobile styling
  const small = isSmall;

  return (
    <Card
      sx={{
        borderRadius: 3,
        background: `linear-gradient(135deg, #232334 60%, #1976d2 100%)`,
        color: "#fff",
        p: { xs: 2, sm: 2 },
        position: "relative",
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        minHeight: 140,
        mb: 1,
      }}
    >
      {/* Mobile: move actions to top-right and increase touch targets */}
      <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 3 }}>
        <Tooltip title="إجراءات" arrow>
          <IconButton
            size={small ? "large" : "medium"}
            onClick={handleMenuOpen}
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              "&:hover": { bgcolor: "#EA7C69", color: "#fff" },
              boxShadow: 3,
              transition: "all 0.2s",
              width: small ? 52 : 44,
              height: small ? 52 : 44,
            }}
          >
            <MoreVertIcon fontSize={small ? "large" : "medium"} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            "& .MuiMenu-paper": {
              borderRadius: 3,
              minWidth: 200,
              boxShadow: "0 8px 32px #23233455",
              bgcolor: "#fff",
              color: "#232334",
            },
            "& .MuiMenuItem-root": {
              fontWeight: 700,
              fontSize: 15,
              px: 2,
              py: 1.2,
              borderRadius: 2,
              "&:hover": {
                bgcolor: "#EA7C69",
                color: "#fff",
                "& .MuiSvgIcon-root": { color: "#fff" },
              },
            },
          }}
        >
          {hasPermission("orders_tables_move_table") && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setEditTableData({
                  ...details,
                  table_id: details.table?.id ?? "",
                  order_id: details.id,
                });
                setEditTableOpen(true);
              }}
            >
              <SwapHorizIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />
              نقل الطاولة
            </MenuItem>
          )}

          {hasPermission("orders_tables_edite_table") && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setEditClientData({
                  ...details,
                  table_id: details.table?.id ?? "",
                  order_id: details.id,
                  error: "❌ فشل في تعديل الأوردر: ",
                });
                setEditClientOpen(true);
              }}
            >
              <EditIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />
              تعديل بيانات العميل
            </MenuItem>
          )}

          {hasPermission("orders_tables_edite_captain_table") && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setEditCaptainData({
                  ...details,
                  table_id: details.table?.id ?? "",
                  captain_order_id: details.captain_order?.id,
                  order_id: details.id,
                  customer_id: details.customer?.id ?? "",
                  error: "❌ فشل في تعديل الأوردر: ",
                });
                setEditCaptainOpen(true);
              }}
            >
              <EditIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />
              تعديل الكابتن
            </MenuItem>
          )}

          {hasPermission("orders_tables_divide_table") && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setDivideOpen(true);
              }}
            >
              <span style={{ fontSize: 20, marginRight: 8 }}>🧩</span>
              تقسيم الطاولة
            </MenuItem>
          )}

          {hasPermission("orders_tables_delete_table") && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onDelete(details.id);
              }}
              sx={{
                color: "#ff4d4f",
                fontWeight: 700,
                "& .MuiSvgIcon-root": { color: "#ff4d4f !important" },
                "&:hover": {
                  bgcolor: "#ff4d4f",
                  color: "#fff",
                },
              }}
            >
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              حذف الطلب
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* MAIN CONTENT: stack vertically on mobile (single column) */}
      <Grid container spacing={2} sx={{ mt: 0.5, flexDirection: small ? "column" : "row" }}>
        <Grid item xs={12} sm={6}>
          <Stack
            spacing={1.2}
            alignItems="flex-start"
            sx={{
              bgcolor: alpha("#232334", 0.18),
              borderRadius: 3,
              p: small ? 2 : 2,
              minHeight: 120,
              boxShadow: "0 2px 8px #23233411",
            }}
          >
            <Typography
              variant={small ? "h6" : "h6"}
              fontWeight={700}
              sx={{
                color: "#EA7C69",
                letterSpacing: 0.5,
                mb: 0.5,
                fontSize: small ? 18 : 20,
              }}
            >
              طاولة {details.table?.name || "-"}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "#fff" }}>
              <strong>العميل:</strong> {customer.name || "-"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.92, color: "#b0bec5" }}>
              <strong>هاتف:</strong> {customer.phone1 || "-"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.92, color: "#b0bec5" }}>
              <strong>الكابتن:</strong> {details.captain_order?.name || "-"}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack
            spacing={1.2}
            alignItems="flex-end"
            sx={{
              bgcolor: alpha("#1976d2", 0.08),
              borderRadius: 3,
              p: small ? 2 : 2,
              minHeight: 120,
              boxShadow: "0 2px 8px #1976d211",
            }}
          >
            <Box sx={{ height: 18 }} />
            <Typography variant="caption" sx={{ color: "#b0bec5", fontWeight: 600 }}>
              {(() => {
                const num = getOrderNumber();
                return `رقم الطلب (${num.label}): #${num.value}`;
              })()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#b0bec5", fontWeight: 600 }}>
              {details.date} - {formatTime12(details.open_time)}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FiberManualRecordIcon
                sx={{
                  color: isUnderReview ? "#ff7043" : "limegreen",
                  fontSize: 13,
                  filter: isUnderReview ? "drop-shadow(0 6px 12px rgba(255,112,67,0.22))" : "none",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isUnderReview ? "#fff" : "#b9f6ca",
                  fontWeight: 700,
                  px: isUnderReview ? 1 : 0,
                  py: isUnderReview ? "2px" : 0,
                  borderRadius: 1,
                  background: isUnderReview ? "linear-gradient(90deg,#ff6b6b 0%,#ff8a65 100%)" : "transparent",
                  boxShadow: isUnderReview ? "0 8px 24px rgba(255,107,107,0.18)" : "none",
                  animation: isUnderReview ? "pulseStatus 1.6s infinite" : "none",
                  fontSize: small ? 14 : 13,
                }}
              >
                {isUnderReview ? "قيد المراجعه" : (statusLabels[currentStatus] || currentStatus || "جاهز للتقديم")}
              </Typography>

              {isUnderReview && (
                <>
                  <Button
                    variant="contained"
                    size={small ? "small" : "small"}
                    onClick={(e) => setConfirmMenuAnchor(e.currentTarget)}
                    disabled={confirmLoading}
                    sx={{
                      ml: 1,
                      bgcolor: "linear-gradient(90deg,#ff8a65 0%,#ff6b6b 100%)",
                      color: "#fff",
                      fontWeight: 800,
                      boxShadow: "0 8px 26px rgba(255,112,67,0.22)",
                      borderRadius: 1.2,
                      px: 1.2,
                      "&:hover": { filter: "brightness(0.95)" },
                    }}
                  >
                    {confirmLoading ? <CircularProgress size={14} color="inherit" /> : "تأكيد"}
                  </Button>

                  <Menu
                    anchorEl={confirmMenuAnchor}
                    open={Boolean(confirmMenuAnchor)}
                    onClose={() => setConfirmMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={() => handleConfirm("pending")} disabled={confirmLoading}>
                      تاكيد كـ "طلب مفتوح"
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      {/* Modals unchanged */}
      <DivideOrderModal open={divideOpen} onClose={() => setDivideOpen(false)} orderId={details.id} items={details.items || []} onSuccess={() => setDivideOpen(false)} />
      <EditOrderFormCaptinOrder
        open={editCaptainOpen}
        onClose={(updated) => {
          setEditCaptainOpen(false);
          if (updated && typeof window.refreshOrderDetails === "function") {
            window.refreshOrderDetails(editCaptainData.order_id);
          }
        }}
        initialData={editCaptainData}
      />
      <EditOrderFormClint open={editClientOpen} onClose={(updated) => { setEditClientOpen(false); if (updated && typeof window.refreshOrderDetails === "function") { window.refreshOrderDetails(editClientData.order_id); } }} initialData={editClientData} />
      <EditOrderFormTableNumber open={editTableOpen} onClose={(updated) => { setEditTableOpen(false); if (updated && typeof window.refreshOrderDetails === "function") { window.refreshOrderDetails(editTableData.order_id); } }} initialData={editTableData} tables={tables} />

      <style>{`@keyframes pulseStatus { 0% { transform: scale(1); box-shadow: 0 6px 18px rgba(255,107,107,0.12); } 50% { transform: scale(1.03); box-shadow: 0 10px 30px rgba(255,107,107,0.28); } 100% { transform: scale(1); box-shadow: 0 6px 18px rgba(255,107,107,0.12); } }`}</style>

      {/* Snackbars */}
      <Snackbar open={confirmSuccess} autoHideDuration={3000} onClose={() => setConfirmSuccess(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}>✅ تم تأكيد الطلب بنجاح</Alert>
      </Snackbar>
      <Snackbar open={Boolean(confirmError)} autoHideDuration={4000} onClose={() => setConfirmError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" sx={{ bgcolor: "#ffebee", color: "#c62828" }}>{confirmError}</Alert>
      </Snackbar>

      {/* Mobile fixed actions bar (render only on small screens) */}
      {small && (
        <Paper elevation={12} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 2500, px: 2, py: 1, display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between", bgcolor: "#fff" }}>
          <Button variant="contained" color="primary" onClick={() => onEdit && onEdit(details)} sx={{ flex: 1, mr: 1, fontWeight: 700 }}>
            تعديل
          </Button>
          <Button variant="outlined" color="error" onClick={() => onDelete && onDelete(details.id)} sx={{ fontWeight: 700 }}>
            حذف
          </Button>
        </Paper>
      )}
    </Card>
  );
};

export default OrderInfoCard;
